import React, { useEffect, useRef, useState } from "react";
import "./styles.css";
import { RootState } from "../../../../store";
import { connect } from "react-redux";
import { ContractAssignmentActionCreator } from "../../../../store/ContractAssignment/actions";
import {
    selectAreaWorthMax,
    selectAreaWorthMin,
    selectContractAssignmentSearchTarget,
    selectContractAssignmentsListCurrentPage,
    selectContractAssignmentsListData,
    selectContractAssignmentsListError,
    selectContractAssignmentsListLastPage,
    selectContractAssignmentsListPending,
    selectDeletingContractAssignmentError,
    selectDeletingContractAssignmentPending,
    selectDeletingContractAssignmentSuccess,
    selectDeletingContractId,
    selectFullPriceMax,
    selectFullPriceMin,
    selectMonthlyPaymentMax,
    selectMonthlyPaymentMin,
    selectRequiredPaymentMax,
    selectRequiredPaymentMin,
    selectSortingByDate,
    selectSortingByName,
} from "../../../../store/ContractAssignment/selectors";
import { ContractAssignmentListItem } from "../ContractAssignmentListItem";
import { selectUserInfo, selectUserPending } from "../../../../store/selectors";
import { ContractAssignmentListSkeleton } from "./components/ContractAssignmentListSkeleton";
import { Error } from "../../../../components/common/Error";
import { ScrollAwareBlock } from "../../../../components/common/ScrollAwareBlock";
import { Button } from "../../../../components/common/Button";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Modal } from "../../../../components/common/Modal";
import { UserActionCreator } from "../../../../store/userActions";
import { HHTabs } from "../../../../components/common/Tabs";
import { RENDER_MODES } from "../../constants";
import { Filters } from "./components/Filters";
import { Search } from "./components/Search";
import debounce from "debounce";
import { Sorting } from "./components/Sorting";

export const _ContractAssignment = ({
                                        userPending,
                                        pending,
                                        deletingPending,
                                        deletingError,
                                        deletingSuccess,
                                        error,
                                        user,
                                        contractAssignmentsList,
                                        lastPage,
                                        currentPage,
                                        deletingContractId,
                                        clearContractAssignments,
                                        getContractAssignments,
                                        getContractAssignmentsByUser,
                                        setDeletingContractId,
                                        deleteContractAssignmentItem,
                                        setNotification,
                                        searchTarget,
                                        byDate,
                                        byName,
                                        fullPriceMin,
                                        fullPriceMax,
                                        areaWorthMin,
                                        areaWorthMax,
                                        requiredPaymentMin,
                                        requiredPaymentMax,
                                        monthlyPaymentMin,
                                        monthlyPaymentMax,
                                    }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const byUser = location?.pathname.includes("by-user");
    const { userId } = useParams();
    const [isFetching, setIsFetching] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(userId);
    const [renderByUserId, setRenderByUserId] = useState(byUser ? 2 : 1);

    // Если selectedUserId не установлен, используем id текущего пользователя
    useEffect(() => {
        if (!selectedUserId && user) {
            setSelectedUserId(user.id);
        }
    }, [user, selectedUserId]);

    const isManualLoadRef = useRef(false);

    // Дебаунс при изменении поискового запроса
    useEffect(() => {
        const load = debounce(() => {
            loadMoreAssignments(1);
        }, 1000);
        load();
        return () => {
            load.clear();
        };
    }, [searchTarget]);

    useEffect(() => {
        loadMoreAssignments(1);
    }, [byDate, byName]);

    useEffect(() => {
        if (
            user &&
            contractAssignmentsList &&
            contractAssignmentsList.length < 1 &&
            !pending &&
            !isFetching &&
            !isManualLoadRef.current
        ) {
            clearContractAssignments();
            loadMoreAssignments(1);
        }
        isManualLoadRef.current = false;
    }, [user, renderByUserId]);

    useEffect(() => {
        if (!pending && !isFetching) {
            clearContractAssignments();
            isManualLoadRef.current = true;
            loadMoreAssignments(1);
        }
    }, [renderByUserId]);

    useEffect(() => {
        if (!!deletingContractId && deletingSuccess) {
            setDeletingContractId(undefined);
            setNotification({
                isOpen: true,
                value: {
                    title: "Объявление удалено",
                    description: "Вы успешно удалили объявление",
                    closeIcon: true,
                },
            });
        }
    }, [deletingSuccess]);

    const loadMoreAssignments = async (page: number) => {
        if (isFetching || pending) return;
        setIsFetching(true);
        if (renderByUserId === 2) {
            await getContractAssignmentsByUser({ userId: selectedUserId, page });
        } else {
            await getContractAssignments({
                page,
                searchTarget,
                byDate,
                byName,
                fullPriceMin,
                fullPriceMax,
                areaWorthMin,
                areaWorthMax,
                requiredPaymentMin,
                requiredPaymentMax,
                monthlyPaymentMin,
                monthlyPaymentMax,
            });
        }
        setIsFetching(false);
    };

    // Intersection Observer для бесконечной подгрузки
    const sentinelRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (
                    entries[0].isIntersecting &&
                    currentPage < lastPage &&
                    !isFetching &&
                    !pending
                ) {
                    loadMoreAssignments(currentPage + 1);
                }
            },
            {
                root: null,
                rootMargin: "200px", // расширяем область срабатывания
                threshold: 0,
            }
        );
        if (sentinelRef.current) {
            observer.observe(sentinelRef.current);
        }
        return () => {
            if (sentinelRef.current) {
                observer.unobserve(sentinelRef.current);
            }
        };
    }, [currentPage, lastPage, isFetching, pending]);

    const handleAdd = () => {
        navigate("/contract-assignment/create");
    };

    if (userPending || (pending && contractAssignmentsList?.length === 0)) {
        return <ContractAssignmentListSkeleton />;
    }

    if (!contractAssignmentsList || error) {
        return (
            <>
                {!byUser && user && (
                    <HHTabs
                        tabs={RENDER_MODES}
                        activeTab={renderByUserId}
                        setActiveTab={setRenderByUserId}
                        className="mt-5 lg:mt-0"
                    />
                )}
                <Error
                    title="Что-то пошло не так"
                    description="Не удалось загрузить объявления. Попробуйте ещё раз позже."
                    buttonText="повторить"
                    retryCallback={() => loadMoreAssignments(1)}
                />
            </>
        );
    }

    return (
        <>
            {!byUser && user && (
                <HHTabs
                    tabs={RENDER_MODES}
                    activeTab={renderByUserId}
                    setActiveTab={setRenderByUserId}
                    className="mt-5 lg:mt-0"
                />
            )}
            <div className="contract-assignment_container">
                {renderByUserId !== 2 && (
                    <>
                        <Search />
                        <div className="contract-assignment-list-toolbar">
                            <Sorting />
                            <Filters
                                sendRequest={() =>
                                    getContractAssignments({
                                        searchTarget,
                                        byDate,
                                        byName,
                                        fullPriceMin,
                                        fullPriceMax,
                                        areaWorthMin,
                                        areaWorthMax,
                                        requiredPaymentMin,
                                        requiredPaymentMax,
                                        monthlyPaymentMin,
                                        monthlyPaymentMax,
                                    })
                                }
                            />
                        </div>
                    </>
                )}
                <ul>
                    {contractAssignmentsList.map((item) => {
                        const isMine = Number(user?.id) === Number(item?.userId);
                        return (
                            <ContractAssignmentListItem
                                key={item.id}
                                isMine={isMine}
                                contractAssignment={item}
                            />
                        );
                    })}
                </ul>
                {isFetching && <ContractAssignmentListSkeleton />}
                {/* Элемент-сентинел, отслеживающий окончание списка */}
                <div ref={sentinelRef} style={{ height: "1px", width: "100%" }}></div>
            </div>
            {!byUser && user && (
                <ScrollAwareBlock
                    className="contract-assignment_add-button"
                    position="bottom"
                >
                    <Button onClick={handleAdd} text="добавить" fullWidth />
                </ScrollAwareBlock>
            )}
            <Modal
                onClose={() => setDeletingContractId(undefined)}
                isOpen={!!deletingContractId}
                title="Удалить объявление?"
                text="Вы уверены, что хотите удалить объявление?"
                actionButtonText="Удалить"
                actionButtonCallback={() =>
                    deleteContractAssignmentItem({ id: deletingContractId })
                }
                actionButtonPending={deletingPending}
            />
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    userPending: selectUserPending(state),
    pending: selectContractAssignmentsListPending(state),
    deletingPending: selectDeletingContractAssignmentPending(state),
    deletingError: selectDeletingContractAssignmentError(state),
    deletingSuccess: selectDeletingContractAssignmentSuccess(state),
    error: selectContractAssignmentsListError(state),
    contractAssignmentsList: selectContractAssignmentsListData(state),
    lastPage: selectContractAssignmentsListLastPage(state),
    currentPage: selectContractAssignmentsListCurrentPage(state),
    user: selectUserInfo(state),
    deletingContractId: selectDeletingContractId(state),
    searchTarget: selectContractAssignmentSearchTarget(state),
    byDate: selectSortingByDate(state),
    byName: selectSortingByName(state),
    fullPriceMin: selectFullPriceMin(state),
    fullPriceMax: selectFullPriceMax(state),
    areaWorthMin: selectAreaWorthMin(state),
    areaWorthMax: selectAreaWorthMax(state),
    requiredPaymentMin: selectRequiredPaymentMin(state),
    requiredPaymentMax: selectRequiredPaymentMax(state),
    monthlyPaymentMin: selectMonthlyPaymentMin(state),
    monthlyPaymentMax: selectMonthlyPaymentMax(state),
});

const mapDispatchToProps = {
    clearContractAssignments:
    ContractAssignmentActionCreator.clearContractAssignments,
    getContractAssignments:
    ContractAssignmentActionCreator.getContractAssignments,
    getContractAssignmentsByUser:
    ContractAssignmentActionCreator.getContractAssignmentsByUser,
    setDeletingContractId: ContractAssignmentActionCreator.setDeletingContractId,
    deleteContractAssignmentItem:
    ContractAssignmentActionCreator.deleteContractAssignmentItem,
    setNotification: UserActionCreator.setNotification,
};

export const ContractAssignment = connect(
    mapStateToProps,
    mapDispatchToProps
)(_ContractAssignment);
